<template>
	<v-form
		class="session-question-form"
		ref="form"
		v-model="valid"
		lazy-validation
		@submit.prevent="submitForm"
	>
		<div class="d-flex justify-space-between flex-wrap align-start">
			<div style="width: 100%">
				<v-textarea
					class="session-question-form__field-input pt-0 rounded"
					color="red"
					v-model="fields.text"
					:rules="rules"
					required
					rows="3"
					placeholder="Type your question..."
				>
				</v-textarea>
				<p
					class="red--text text-center"
					v-for="error in eventErrors"
					:key="error"
					style="width: 100%"
				>
					{{ error }}
				</p>
			</div>
			<div v-if="isAnonymous" class="session-question-form__anonymous-wrapper mt-1 mb-1">
				<span class="session-question-form__anonymous-wrapper__text">Ask Anonymously</span>
				<div
					@click="toggleStatus"
					:style="{
						backgroundColor: !isStatus ? '#9b9b9b' : '#34c759',
					}"
					class="session-question-form__anonymous-wrapper__button"
				>
					<div :style="{ transform: isStatus ? 'translateX(20px)' : 'translateX(0)' }"
               class="session-question-form__anonymous-wrapper__button__circle"></div>
				</div>
			</div>

			<div class="session-question-form__buttons-wrapper mt-3" style="width: 100%">
				<button
					type="button"
					@click="closeModal"
					class="session-question-form__buttons-wrapper__cancel"
				>
					Cancel
				</button>
				<button
					type="submit"
					class="session-question-form__buttons-wrapper__send"
				>
					<v-progress-circular color="white" v-if="isLoading"></v-progress-circular>
					<span v-else>Send</span>
				</button>
			</div>
		</div>
	</v-form>
</template>

<script>
import { ruleRequired } from '@/utils/rules'
import Form from '@/mixins/Form'
import { mapActions, mapGetters } from 'vuex'
export default {
	mixins: [Form],
	props: {
		profile: {
			type: Object,
			default: () => ({}),
		},
		qaIsAutocomplete: {
			type: Boolean,
			default: false,
		},
		isQuestionsPage: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			isStatus: false,
			fields: {
				text: '',
			},
		}
	},
	methods: {
		...mapActions('event', ['addSessionQuestion', 'getSession', 'getSessionQuestions']),
		...mapActions('auth', ['showAlert']),
		toggleStatus() {
			this.isStatus = !this.isStatus
		},

		async submitForm() {
      if (this.fields.text === ''){
        return this.showAlert({message: 'Question cannot be blank.', type: 'error'})
      }
      if(this.fields.text.length > 500){
        return this.showAlert({ message: 'Maximum 500 characters allowed.', type: 'error' })
      }
			let qa_status = this.isStatus ? 1 : 0
			await this.addSessionQuestion({
				qna_entity_type: this.$route.query.qna_entity_type,
				qna_entity_id: this.$route.query.qna_entity_id,
				qna_id: this.$route.query.qna_id,
				qna_question_text: this.fields.text,
				user_id: this.profile.user_id,
        qna_allow_anonymous: qa_status,
			})
		},
		closeModal() {
			this.$emit('closeModal')
			this.$refs.form.reset()
		},
	},
	computed: {
		...mapGetters('event', ['eventErrors', 'isLoading']),
		isAnonymous() {
			return Number(this.$route.query.qna) === 1
		},

		rules() {
			return [
				(v) => ruleRequired(v) || 'Field is required.',
				(v) => (v && v.trim().length > 0) || 'Question cannot be blank.',
				(v) => (v && v.length <= 500) || 'Maximum 500 characters allowed.',
			]
		},
	},
	watch: {
		isLoading(newValue, oldValue) {
			if (!newValue && oldValue) {
				!this.eventErrors.length && this.closeModal()
				this.$emit('updateList')
			}
		},
	},
}
</script>

<style lang="scss">
.session-question-form {
	font-family: 'Inter', sans-serif;
	&__buttons-wrapper {
		display: flex;
		align-items: center;
		gap: 16px;
		justify-content: center;
		&__cancel {
			width: 150px;
			height: 46px;
			display: flex;
			justify-content: center;
			align-items: center;
			border-radius: 8px;
			border: 1.5px solid var(--button-secondary-background-color);
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
			color: var(--button-secondary-background-color);
		}
		&__send {
			width: 134px;
			height: 46px;
			justify-content: center;
			align-items: center;
			border-radius: 8px;
			background-color: var(--button-main-background-color);
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
			color: var(--button-main-color);
		}
	}
	&__anonymous-wrapper {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 12px;
		border-radius: 8px;
		border: 1px solid #e2e2e2;
		&__text {
			font-weight: 500;
			font-size: 18px;
			line-height: 100%;
			color: #4a4a4a;
		}
		&__button {
			display: flex;
			align-items: center;
			height: 31px;
			width: 51px;
			border-radius: 100px;
			padding: 2px;
			cursor: pointer;
      transition: background-color 0.3s ease, justify-content 0.3s ease;
			&__circle {
				background-color: #ffffff;
				width: 27px;
				height: 27px;
				border-radius: 100px;
        transition: transform 0.3s ease;
			}
		}
	}
	&__field-input {
		textarea {
			border: 1px solid #e2e2e2;
			border-radius: 5px;
			padding: 12px;
			resize: none;
			min-height: 134px !important;

			&::placeholder {
				color: #9b9b9b !important;
				opacity: 1;
				font-weight: 400;
				font-size: 18px;
				line-height: 100%;
				letter-spacing: 0;
			}
		}
	}

	&__button {
		border-width: 2px;
		border-radius: 8px;
		font-size: 16px !important;
		font-weight: 700;
		text-transform: capitalize;
		padding: 10px 38px !important;

		@media (max-width: 480px) {
			padding: 0 !important;
			flex-grow: 1;
		}
	}

	&__buttonCancel {
		color: var(--button-secondary-background-color) !important;
		background: #fff !important;
		margin-right: 8px;
		border: 2px solid var(--button-secondary-background-color);
	}

	&__buttonSave {
		background: var(--button-secondary-background-color) !important;
		color: #fff !important;
		margin-left: 8px;
	}
}
</style>
